import { DEFAULT_ANSWER_COLUMN_PROPERTY, getUpdatedFormatConfig, } from '@thoughtspot/client-state-util';
import { upgraderFn } from '@thoughtspot/client-state-util/esm/chart-client-state/upgraders/v4dot2-to-v5';
import { upgraderFn as tableUpgraderFn } from '@thoughtspot/client-state-util/esm/table-client-state/upgraders/v1-to-v2';
import { getFormatPattern, isColumnCurrencyFormatType, } from '@thoughtspot/formatter-service';
import { translate } from '@thoughtspot/i18n';
import { getCustomChartSettingsSchema, getGrowthColumnFormatConfig, getVisualizedColumnsForCustomChart, SettingsMenuItems, shouldRenderPropElements, } from '@thoughtspot/ts-chart-app';
import { STORED_TO_UPGRADED_NAMESPACE } from '@thoughtspot/ts-chart-app/lib/utils/chart-settings/store-key-namespaces';
import { isGrowth, isMeasureName, isMeasureValues, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { isLegendEnabled } from '../../components/chart-legend-utils';
import { isShowAnomaliesSupported } from '../../components/kpi/cortex-utils';
import { getCustomComparisonOptions } from '../../components/kpi/kpi-utils';
import { getAxes, getAxisForColumn, getChartType, getColumn, getEffectiveColumnType, getSortedColumnsId, getVisualizedColumns, getVisualizedColumnsForKpi, getYAxes, isCustomOrderDefined, isSorted, } from '../../core/chart-model-utils';
import { configOptions, isCustomOrTsHostedChart, } from '../../core/chart-type-specification-service';
import { isKPIChartEnhancementsEnabled } from '@thoughtspot/session-service';
import { getAggregationTypeLabelsMap, getOriginalColumnName, } from '../../core/column-name-util';
import { getLegendCardinality } from '../../data/data-util';
import { SummaryModeStringPath } from '../../devextreme/model/pivot-data-model';
import { AccessMode, AxisTypes, ChartType, DisplayMode, } from '../../types';
import { createLogger } from '../../utils/logger';
import { getTimeBucketMenuItems } from '../../utils/menu.util';
import { timeBuckets } from '@thoughtspot/date-format-util';
import { areaChartSettingsSchema } from './area';
import { barChartSettingsSchema } from './bar';
import { bubbleChartSettingsSchema } from './bubble';
import { candlestickChartSettingsSchema } from './candlestick';
import { ChartSettingsElementType, ChartSettingsV2MenuItems, CustomAPIHandler, } from './chart-settings-v2-schema.types';
import { columnChartSettingsSchema } from './column';
import { TabItemSkeleton, TabSkeleton } from './common-skeletons';
import { getFormatPatternForAxis } from './common-utils';
import { funnelChartSettingsSchema } from './funnel';
import { geoAreaChartSettingsSchema } from './geo-area';
import { geoBubbleChartSettingsSchema } from './geo-bubble';
import { geoHeatmapChartSettingsSchema } from './geo-heatmap';
import { heatmapChartSettingsSchema } from './heatmap';
import { getKpiChartSettingsSchema } from './kpi';
import { lineChartSettingsSchema } from './line';
import { lineColumnChartSettingsSchema } from './line-column';
import { lineStackedColumnChartSettingsSchema } from './line-stacked-column';
import { paretoChartSettingsSchema } from './pareto';
import { pieChartSettingsSchema } from './pie';
import { pivotTableSettingsSchema } from './pivot';
import { radarChartSettingsSchema } from './radar';
import { sankeyChartSettingsSchema } from './sankey';
import { scatterChartSettingsSchema } from './scatter';
import { stackedAreaChartSettingsSchema } from './stacked-area';
import { stackedBarSchema } from './stacked-bar';
import { stackedColumnChartSettingsSchema } from './stacked-column';
import { tableSettingsSchema } from './table/table-schema';
import { treemapChartSettingsSchema } from './treemap';
import { waterfallChartSettingsSchema } from './waterfall';
const logger = createLogger('chart-viz-component');
const EXCLUDED_ELEMENTS_FOR_VALUES = [
    ChartSettingsElementType.SECTION,
    ChartSettingsElementType.ACCORDION,
    ChartSettingsElementType.ACCORDION_ITEM,
];
export const addRenderKey = (setting, column, index) => {
    if (setting.key) {
        setting.renderKey = `${column.id}--${setting.key}`;
    }
    else {
        const elementType = setting.elementType;
        const key = `${column === null || column === void 0 ? void 0 : column.id}--${elementType}--${index}`;
        setting.renderKey = key;
    }
    return setting;
};
export const addScrollToColumnAttribute = (setting, columnId, selectedColumnId) => {
    var _a, _b, _c;
    if (!selectedColumnId || columnId !== selectedColumnId) {
        return;
    }
    const columnAccordionItem = (_c = (_b = (_a = setting.children) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.children) === null || _c === void 0 ? void 0 : _c[0];
    if ((columnAccordionItem === null || columnAccordionItem === void 0 ? void 0 : columnAccordionItem.elementType) ===
        ChartSettingsElementType.ACCORDION_ITEM) {
        columnAccordionItem.properties = {
            ...columnAccordionItem.properties,
            'data-scroll-to-column': selectedColumnId,
        };
    }
};
export const addMetadata = (setting, column, axis, properties, fallBackProperties) => {
    const metadata = {};
    if (setting.key === 'dataSize' || setting.key === 'genericCompareValue') {
        metadata.customAPIHandler = CustomAPIHandler.DataSizeUpdate;
    }
    if (column) {
        metadata.columnId = column.id;
    }
    if (axis) {
        metadata.axisId = axis.id;
    }
    if (setting.isAnswerProperty) {
        switch (setting.key) {
            case 'timeBucket':
                metadata.customAPIHandler = CustomAPIHandler.UpdateColumn;
                metadata.getVariables = (value) => {
                    return {
                        columnId: column.id,
                        columnConfig: {
                            timeBucket: value,
                        },
                    };
                };
                break;
            case 'aggregationType':
                metadata.customAPIHandler = CustomAPIHandler.UpdateColumn;
                metadata.getVariables = (value) => {
                    return {
                        columnId: column.id,
                        columnConfig: {
                            aggregationType: value,
                        },
                    };
                };
                break;
            case 'name':
                metadata.customAPIHandler = CustomAPIHandler.RenameColumn;
                metadata.getVariables = (value) => {
                    return {
                        columnId: column.id,
                        name: value,
                    };
                };
                break;
            case 'columnProps.columnProperties.format':
                metadata.customAPIHandler =
                    CustomAPIHandler.UpdateAnswerColumnClientState;
                metadata.getVariables = (value) => {
                    return {
                        ...column.columnProps,
                        columnProperties: {
                            ...DEFAULT_ANSWER_COLUMN_PROPERTY,
                            ...column.columnProps.columnProperties,
                            format: getUpdatedFormatConfig(value),
                        },
                    };
                };
                break;
            case 'columnProps.columnProperties.sortConfig':
                metadata.customAPIHandler =
                    CustomAPIHandler.UpdateAnswerColumnClientState;
                metadata.getVariables = (value) => {
                    return {
                        ...column.columnProps,
                        columnProperties: {
                            ...DEFAULT_ANSWER_COLUMN_PROPERTY,
                            ...column.columnProps.columnProperties,
                            sortConfig: value,
                        },
                    };
                };
                break;
            case 'headlineVisibility':
                metadata.customAPIHandler =
                    CustomAPIHandler.UpdateHeadlineVisibility;
                metadata.getVariables = (value) => {
                    return {
                        columnId: column.id,
                        visible: value,
                    };
                };
                break;
            case 'allHeadlineVisibility':
                metadata.customAPIHandler =
                    CustomAPIHandler.UpdateAllHeadlineVisibility;
                metadata.getVariables = (value) => {
                    return {
                        visible: value,
                    };
                };
                break;
            case 'summaryMode':
                metadata.customAPIHandler = CustomAPIHandler.UpdateColumn;
                metadata.getVariables = (value) => {
                    return {
                        columnId: column.id,
                        columnConfig: {
                            summaryMode: value,
                        },
                    };
                };
                break;
            case 'columnProperty.summariesNumberFormatConfig':
                metadata.customAPIHandler =
                    CustomAPIHandler.UpdateHeadlineVizClientState;
                metadata.getVariables = (value) => {
                    return {
                        format: getUpdatedFormatConfig(value),
                    };
                };
                break;
            default:
                break;
        }
    }
    setting.metadata = metadata;
};
export const getRunTimeProperties = (currentSetting, column, properties, propertyKey, valuePath, chartModel, tableViz, chart) => {
    var _a, _b, _c;
    const dataModel = chart === null || chart === void 0 ? void 0 : chart.getDataModel();
    if (valuePath === 'TS_INPUT') {
        switch (propertyKey) {
            case 'optionsGroups':
                if (currentSetting.key === 'aggregationType') {
                    const aggregates = getAggregationTypeLabelsMap(properties);
                    const options = Object.entries(aggregates).map(([key, label]) => ({ id: key, label }));
                    return [{ options }];
                }
                if (currentSetting.key === 'summaryMode') {
                    const optionKeys = Object.keys(SummaryModeStringPath);
                    const options = optionKeys.map(key => {
                        return {
                            id: key,
                            label: translate(SummaryModeStringPath[key]),
                        };
                    });
                    return [{ options }];
                }
                if (currentSetting.key === 'customCompare') {
                    return getCustomComparisonOptions(chartModel, isKPIChartEnhancementsEnabled());
                }
                break;
            case 'typeProps.value':
                if (currentSetting.key === 'format') {
                    return (_b = (_a = properties.columnProps.columnProperties) === null || _a === void 0 ? void 0 : _a.format) === null || _b === void 0 ? void 0 : _b.category;
                }
                break;
            case 'disabled': {
                if (currentSetting.key ===
                    'columnProps.columnProperties.sortConfig') {
                    return (!isSorted(chartModel, column) ||
                        !isCustomOrderDefined(column));
                }
                if (currentSetting.key === 'enabled') {
                    return (!!dataModel &&
                        getLegendCardinality(chartModel, dataModel) <= 1);
                }
                if (currentSetting.key === 'position') {
                    const isLegendDisabled = !dataModel ||
                        getLegendCardinality(chartModel, dataModel) <= 1;
                    const legendEnabled = isLegendEnabled(chartModel);
                    return (isLegendDisabled ||
                        !(_.isNil(legendEnabled) || legendEnabled));
                }
                if ([
                    'regressionLine.enabled',
                    'regressionLine.lineStyle',
                ].includes(currentSetting.key)) {
                    const series = _.get(dataModel, 'series', []);
                    return series.length !== 1;
                }
                break;
            }
            case 'schema': {
                if (currentSetting.key === 'timeBucket') {
                    return [
                        { items: getTimeBucketMenuItems(column, translate) },
                    ];
                }
                break;
            }
            case 'header': {
                if (currentSetting.key === 'columnName') {
                    return getOriginalColumnName(column);
                }
                break;
            }
            case 'value': {
                if (currentSetting.key === 'headlineVisibility') {
                    return (_c = properties === null || properties === void 0 ? void 0 : properties.visibleSummaryColumnIds) === null || _c === void 0 ? void 0 : _c.includes(column.id);
                }
                if (currentSetting.key === 'allHeadlineVisibility') {
                    return (properties === null || properties === void 0 ? void 0 : properties.hiddenSummaryColumnIds.length) === 0;
                }
                break;
            }
            case 'indeterminate': {
                if (currentSetting.key === 'showAnomalies') {
                    return !isShowAnomaliesSupported(chartModel);
                }
                break;
            }
            case 'customFormatPattern': {
                if ([
                    'columnProps.columnProperties.format',
                    'columnProperty.summariesNumberFormatConfig',
                ].includes(currentSetting.key)) {
                    return getFormatPattern(column);
                }
                if (currentSetting.key === 'format') {
                    const axis = getAxisForColumn(chartModel, column.id);
                    return getFormatPatternForAxis(chartModel, axis.id);
                }
                break;
            }
            case 'isCustomCurrency': {
                if ([
                    'columnProps.columnProperties.format',
                    'columnProperty.summariesNumberFormatConfig',
                ].includes(currentSetting.key)) {
                    return isColumnCurrencyFormatType(column);
                }
                break;
            }
            case 'typeProps': {
                if (currentSetting.key === 'columnProps.columnProperties.format') {
                    return {
                        ariaLabel: 'number-type',
                        disabled: isGrowth(column),
                    };
                }
                break;
            }
            default:
                return null;
        }
    }
    else if (valuePath === 'CUSTOM') {
        return currentSetting.getRunTimeProperties[propertyKey]({
            currentProperties: currentSetting.properties.value,
            chartModel,
            dataModel,
            columnId: column === null || column === void 0 ? void 0 : column.id,
        });
    }
    return (_.get(properties, valuePath) ||
        _.get(column, valuePath));
};
export const updateRunTimeProperties = (setting, column, properties, rootProperties, chartModel, tableViz, chart, axis) => {
    var _a;
    const stack = [setting];
    while (stack.length > 0) {
        const currentSetting = stack.pop();
        let fallBackProperties = null;
        if (currentSetting.fallbackPropertySelector) {
            fallBackProperties = Object.values(rootProperties).find(properties => _.get(properties, currentSetting.fallbackPropertySelector.key) === currentSetting.fallbackPropertySelector.value);
        }
        addMetadata(currentSetting, column, axis, properties, fallBackProperties);
        const runTimeProperties = currentSetting.runTimeProperties;
        if (!_.isEmpty(runTimeProperties)) {
            Object.entries(runTimeProperties).forEach(([key, valuePath]) => {
                _.set(currentSetting.properties, key, getRunTimeProperties(currentSetting, column, properties, key, valuePath, chartModel, tableViz, chart));
            });
        }
        if ((_a = currentSetting.children) === null || _a === void 0 ? void 0 : _a.length) {
            stack.push(...currentSetting.children);
        }
    }
    return setting;
};
const resolveStoreKeyBinding = (currentSetting, vizLevelValuePathPrefix, hasRootProperty) => {
    var _a;
    if (!currentSetting.storeKey ||
        currentSetting.isAnswerProperty ||
        ((_a = currentSetting.children) === null || _a === void 0 ? void 0 : _a.length)) {
        return null;
    }
    const [storedNamespace, ...rest] = currentSetting.storeKey.split('.');
    const upgradedNamespace = STORED_TO_UPGRADED_NAMESPACE[storedNamespace];
    if (!upgradedNamespace ||
        !rest.length ||
        rest.some(segment => !segment.length)) {
        return null;
    }
    const upgradedPath = [upgradedNamespace, ...rest].join('.');
    if (upgradedPath.startsWith(`${vizLevelValuePathPrefix}.`)) {
        return {
            readKey: upgradedPath.slice(vizLevelValuePathPrefix.length + 1),
            readOnRoot: false,
            valuePath: upgradedPath,
        };
    }
    return hasRootProperty
        ? { readKey: upgradedPath, readOnRoot: true, valuePath: upgradedPath }
        : null;
};
const COLUMN_NUMBER_FORMAT_KEY = 'columnProps.columnProperties.format';
export const getValue = (chart, chartModel, tableViz, allSummaryVizes, column, properties, currentSetting, answerLevelProperty, storeKeyReadKey) => {
    var _a, _b, _c;
    const value = (_b = (_a = _.get(properties, storeKeyReadKey !== null && storeKeyReadKey !== void 0 ? storeKeyReadKey : currentSetting.key)) !== null && _a !== void 0 ? _a : (storeKeyReadKey != null
        ? undefined
        : _.get(answerLevelProperty, currentSetting.key))) !== null && _b !== void 0 ? _b : (_c = currentSetting.properties) === null || _c === void 0 ? void 0 : _c.value;
    switch (currentSetting.key) {
        case 'timeBucket':
            return translate(`timeBucketLabels.${timeBuckets[value]}`);
        case COLUMN_NUMBER_FORMAT_KEY:
            if (!currentSetting.parseValueToComponent &&
                column &&
                isGrowth(column)) {
                return getGrowthColumnFormatConfig(value);
            }
            break;
        default:
            break;
    }
    if (currentSetting.parseValueToComponent) {
        return currentSetting.parseValueToComponent({
            chart,
            chartModel,
            tableViz,
            allSummaryVizes,
            column,
            value,
        });
    }
    return value;
};
export const updateValues = (chart, chartModel, tableViz, allSummaryVizes, column, setting, vizLevelProperty, vizLevelValuePathPrefix, answerLevelProperty, answerLevelValuePathPrefix, vizLevelRootProperty) => {
    var _a;
    const stack = [setting];
    while (stack.length > 0) {
        const currentSetting = stack.pop();
        const properties = currentSetting.isAnswerProperty
            ? answerLevelProperty
            : vizLevelProperty;
        if (currentSetting.key &&
            !EXCLUDED_ELEMENTS_FOR_VALUES.includes(currentSetting.elementType)) {
            const storeKeyBinding = resolveStoreKeyBinding(currentSetting, vizLevelValuePathPrefix, vizLevelRootProperty != null);
            currentSetting.properties = {
                ...(currentSetting.properties || {}),
                value: getValue(chart, chartModel, tableViz, allSummaryVizes, column, (storeKeyBinding === null || storeKeyBinding === void 0 ? void 0 : storeKeyBinding.readOnRoot)
                    ? vizLevelRootProperty
                    : properties, currentSetting, answerLevelProperty, storeKeyBinding === null || storeKeyBinding === void 0 ? void 0 : storeKeyBinding.readKey),
            };
            if (currentSetting.isAnswerProperty) {
                currentSetting.valuePath = `${answerLevelValuePathPrefix}.${currentSetting.key}`;
            }
            else {
                currentSetting.valuePath = storeKeyBinding
                    ? storeKeyBinding.valuePath
                    : `${vizLevelValuePathPrefix}.${currentSetting.key}`;
            }
        }
        if ((_a = currentSetting.children) === null || _a === void 0 ? void 0 : _a.length) {
            stack.push(...currentSetting.children);
        }
    }
    return setting;
};
export const applyTranslations = (setting) => {
    var _a, _b, _c, _d;
    const stack = [setting];
    while (stack.length > 0) {
        const currentSetting = stack.pop();
        if (currentSetting.translate) {
            currentSetting.translate.forEach(path => {
                if (path === 'optionsGroups.options.label') {
                    const optionsGroups = currentSetting.properties.optionsGroups;
                    if (optionsGroups) {
                        optionsGroups.forEach(group => {
                            if (group.options) {
                                group.options.forEach(option => {
                                    if (option.label) {
                                        option.label = translate(option.label);
                                    }
                                });
                            }
                        });
                    }
                }
                else {
                    const fullPath = `properties.${path}`;
                    const key = _.get(currentSetting, fullPath);
                    if (key) {
                        _.set(currentSetting, fullPath, translate(key));
                    }
                }
            });
        }
        const labelTranslation = (_a = currentSetting.properties) === null || _a === void 0 ? void 0 : _a.labelTranslation;
        if (labelTranslation) {
            const translated = translate(labelTranslation);
            if (translated && translated !== labelTranslation) {
                if ('label' in currentSetting.properties) {
                    currentSetting.properties.label = translated;
                }
                if ('name' in currentSetting.properties) {
                    currentSetting.properties.name = translated;
                    if ('nameSuffix' in currentSetting.properties) {
                        currentSetting.properties.name += currentSetting.properties.nameSuffix;
                    }
                }
                if ('header' in currentSetting.properties) {
                    currentSetting.properties.header = translated;
                }
            }
        }
        const disabledTooltipTranslation = (_b = currentSetting.properties) === null || _b === void 0 ? void 0 : _b.disabledTooltipContentTranslation;
        if (disabledTooltipTranslation) {
            const translated = translate(disabledTooltipTranslation);
            if (translated && translated !== disabledTooltipTranslation) {
                currentSetting.properties.disabledTooltipContent = translated;
            }
        }
        if (currentSetting.properties) {
            for (const subProp of Object.values(currentSetting.properties)) {
                if (subProp &&
                    typeof subProp === 'object' &&
                    !Array.isArray(subProp) &&
                    subProp.tooltipTextTranslation) {
                    const ttTranslation = subProp
                        .tooltipTextTranslation;
                    const translated = translate(ttTranslation);
                    if (translated && translated !== ttTranslation) {
                        subProp.tooltipText = translated;
                    }
                }
            }
        }
        const optionsGroups = (_c = currentSetting.properties) === null || _c === void 0 ? void 0 : _c.optionsGroups;
        if (optionsGroups) {
            optionsGroups.forEach(group => {
                if (group.options) {
                    group.options.forEach(option => {
                        if (option.labelTranslation) {
                            const translated = translate(option.labelTranslation);
                            if (translated &&
                                translated !== option.labelTranslation) {
                                option.label = translated;
                            }
                        }
                    });
                }
            });
        }
        if ((_d = currentSetting.children) === null || _d === void 0 ? void 0 : _d.length) {
            stack.push(...currentSetting.children);
        }
    }
    return setting;
};
export const applyVisibilityRules = (context) => {
    var _a, _b;
    const { setting, answerLevelProperty, currentColumn, currentAxis, areAdvancedSettingsVisible, requiredClientStateProperties, chartModel, chart, setShowAdvancedToggle, permission, } = context;
    const stack = [setting];
    let hasAdvancedSettings = false;
    while (stack.length > 0) {
        const currentSetting = stack.pop();
        if (currentSetting.isAdvanced) {
            hasAdvancedSettings = true;
        }
        if (currentSetting.isAdvanced && !areAdvancedSettingsVisible) {
            currentSetting.isHidden = true;
            continue;
        }
        if (!currentSetting.isHidden && currentSetting.isHiddenCallback) {
            currentSetting.isHidden = currentSetting.isHiddenCallback({
                chartModel,
                currentColumn,
                currentAxis,
                requiredClientStateProperties,
                chart,
                answerLevelProperty,
                permission,
            });
        }
        if (!currentSetting.isHidden && currentSetting.isDisabledCallback) {
            const { disabled, tooltip } = currentSetting.isDisabledCallback({
                chartModel,
                currentColumn,
                currentAxis,
                requiredClientStateProperties,
                permission,
                chart,
            });
            if (disabled) {
                currentSetting.properties.disabled = disabled;
                if (tooltip) {
                    currentSetting.properties.disabledTooltipContent = tooltip;
                }
            }
        }
        if (currentSetting.key === 'aggregationType' && (permission === null || permission === void 0 ? void 0 : permission.dataSourceAccessLevel) === AccessMode.NoAccess) {
            currentSetting.properties = (_a = currentSetting.properties) !== null && _a !== void 0 ? _a : {};
            currentSetting.properties.disabled = true;
        }
        if (!currentSetting.isHidden && ((_b = currentSetting.children) === null || _b === void 0 ? void 0 : _b.length)) {
            stack.push(...currentSetting.children);
        }
    }
    if (setShowAdvancedToggle && hasAdvancedSettings) {
        setShowAdvancedToggle(true);
    }
    return setting;
};
export const filterVisibleSettings = (setting) => {
    var _a;
    if (setting.isHidden) {
        return false;
    }
    if ((_a = setting.children) === null || _a === void 0 ? void 0 : _a.length) {
        setting.children = setting.children.filter(filterVisibleSettings);
    }
    return true;
};
export function customChartSettingsSchema(settingsSchema, clientStateProperties, chartModel, chart, areAdvancedSettingsVisible, path, setShowAdvancedToggle, clientStateRootProperties) {
    const settings = _.cloneDeep(settingsSchema).map((setting) => {
        applyTranslations(setting);
        updateValues(chart, chartModel, null, null, null, setting, clientStateProperties, path, undefined, undefined, clientStateRootProperties);
        updateRunTimeProperties(setting, null, clientStateProperties, null, chartModel, null, chart, null);
        return applyVisibilityRules({
            setting,
            currentColumn: undefined,
            currentAxis: undefined,
            areAdvancedSettingsVisible,
            requiredClientStateProperties: clientStateProperties,
            chartModel,
            chart,
            setShowAdvancedToggle,
        });
    });
    return settings;
}
export const getChartSettingsSchema = (chartModel, chartSettingItem) => {
    const chartType = getChartType(chartModel);
    switch (chartType) {
        case ChartType.Bar:
            return barChartSettingsSchema[chartSettingItem];
        case ChartType.StackedBar:
            return stackedBarSchema[chartSettingItem];
        case ChartType.Column:
            return columnChartSettingsSchema[chartSettingItem];
        case ChartType.Line:
            return lineChartSettingsSchema[chartSettingItem];
        case ChartType.LineStackedColumn:
            return lineStackedColumnChartSettingsSchema[chartSettingItem];
        case ChartType.StackedColumn:
            return stackedColumnChartSettingsSchema[chartSettingItem];
        case ChartType.LineColumn:
            return lineColumnChartSettingsSchema[chartSettingItem];
        case ChartType.Funnel:
            return funnelChartSettingsSchema[chartSettingItem];
        case ChartType.Candlestick:
            return candlestickChartSettingsSchema[chartSettingItem];
        case ChartType.PivotTable:
            return pivotTableSettingsSchema[chartSettingItem];
        case ChartType.Waterfall:
            return waterfallChartSettingsSchema[chartSettingItem];
        case ChartType.Area:
            return areaChartSettingsSchema[chartSettingItem];
        case ChartType.Bubble:
            return bubbleChartSettingsSchema[chartSettingItem];
        case ChartType.StackedArea:
            return stackedAreaChartSettingsSchema[chartSettingItem];
        case ChartType.Scatter:
            return scatterChartSettingsSchema[chartSettingItem];
        case ChartType.Pareto:
            return paretoChartSettingsSchema[chartSettingItem];
        case ChartType.GeoHeatmap:
            return geoHeatmapChartSettingsSchema[chartSettingItem];
        case ChartType.Heatmap:
            return heatmapChartSettingsSchema[chartSettingItem];
        case ChartType.GeoArea:
            return geoAreaChartSettingsSchema[chartSettingItem];
        case ChartType.GeoBubble:
            return geoBubbleChartSettingsSchema[chartSettingItem];
        case ChartType.SpiderWeb:
            return radarChartSettingsSchema[chartSettingItem];
        case ChartType.Pie:
            return pieChartSettingsSchema[chartSettingItem];
        case ChartType.Sankey:
            return sankeyChartSettingsSchema[chartSettingItem];
        case ChartType.Treemap:
            return treemapChartSettingsSchema[chartSettingItem];
        case ChartType.Kpi:
            return getKpiChartSettingsSchema(chartModel)[chartSettingItem];
        default:
            logger.warn(`Unsupported chart type: ${chartType}`);
            return [];
    }
};
export function getColumnSettings(columnSettingSchema, column, isCustomChart) {
    if (isCustomChart) {
        const foundColumn = columnSettingSchema.find(col => Object.keys(col).includes(column.id));
        return foundColumn ? foundColumn[column.id] : undefined;
    }
    return columnSettingSchema.find((columnSettings) => columnSettings.type === getEffectiveColumnType(column));
}
export const getColumnSettingSchema = (chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, allowedCustomConfigurations, setShowAdvancedToggle, permission, selectedColumnId) => {
    const finalSettings = [];
    let columnSettingSchema;
    let visualizedColumns;
    const chartType = getChartType(chartModel);
    const isCustomChart = isCustomOrTsHostedChart(chartType);
    const renderPropElements = shouldRenderPropElements(visualPropEditorDefinition);
    if (isCustomChart) {
        columnSettingSchema = getCustomChartSettingsSchema(visualPropEditorDefinition, SettingsMenuItems.COLUMN, allowedCustomConfigurations);
        visualizedColumns = getVisualizedColumnsForCustomChart(chartModel);
    }
    else {
        columnSettingSchema = getChartSettingsSchema(chartModel, ChartSettingsV2MenuItems.COLUMN);
        const unfilteredVisualizedColumns = chartType === ChartType.Kpi
            ? getVisualizedColumnsForKpi(chartModel)
            : getVisualizedColumns(chartModel);
        visualizedColumns = unfilteredVisualizedColumns.filter(col => !(isMeasureName(col) || isMeasureValues(col)));
    }
    const upgradedClientState = upgraderFn(chartModel, customVisualProps, renderPropElements);
    visualizedColumns === null || visualizedColumns === void 0 ? void 0 : visualizedColumns.forEach((column) => {
        const columnSettings = getColumnSettings(columnSettingSchema, column, isCustomChart);
        let axis = getAxisForColumn(chartModel, column.id);
        if (isCustomChart) {
            axis = null;
        }
        const currentProperties = upgradedClientState.columnProperties[column.id];
        if (columnSettings === null || columnSettings === void 0 ? void 0 : columnSettings.settings) {
            const settings = _.cloneDeep(columnSettings.settings).map((setting) => {
                applyTranslations(setting);
                updateValues(chart, chartModel, null, null, column, setting, currentProperties, `columnProperties.${column.id}`, column, `columns[0].${column.id}`, upgradedClientState);
                updateRunTimeProperties(setting, column, column, null, chartModel, null, chart, axis);
                return applyVisibilityRules({
                    setting,
                    currentColumn: column,
                    currentAxis: axis,
                    areAdvancedSettingsVisible,
                    requiredClientStateProperties: undefined,
                    chartModel,
                    chart,
                    setShowAdvancedToggle,
                    permission,
                });
            });
            const filteredSettings = settings
                .filter(filterVisibleSettings)
                .map((setting, index) => {
                const settingWithKey = addRenderKey(setting, column, index);
                addScrollToColumnAttribute(settingWithKey, column.id, selectedColumnId);
                return settingWithKey;
            });
            finalSettings.push(...filteredSettings);
        }
    });
    return finalSettings;
};
export const getDataLabelSettingSchema = (chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, setShowAdvancedToggle, permission) => {
    const finalSettings = [];
    const chartType = getChartType(chartModel);
    const isCustomChart = isCustomOrTsHostedChart(chartType);
    const renderPropElements = shouldRenderPropElements(visualPropEditorDefinition);
    const upgradedClientState = upgraderFn(chartModel, customVisualProps, renderPropElements);
    if (isCustomChart) {
        const dataLabelSettingSchema = getCustomChartSettingsSchema(visualPropEditorDefinition, SettingsMenuItems.DATA_LABEL);
        if (dataLabelSettingSchema) {
            const settings = customChartSettingsSchema(dataLabelSettingSchema, upgradedClientState.dataLabelProperties, chartModel, chart, areAdvancedSettingsVisible, 'dataLabelProperties', setShowAdvancedToggle, upgradedClientState);
            finalSettings.push(...settings.filter(filterVisibleSettings));
        }
        return finalSettings;
    }
    const dataLabelSettingSchema = getChartSettingsSchema(chartModel, ChartSettingsV2MenuItems.DATA_LABEL);
    if (dataLabelSettingSchema) {
        const chartLevelDataLabelSchema = dataLabelSettingSchema.filter((setting) => setting.isChartProperty);
        const currentProperties = upgradedClientState.dataLabelProperties;
        const settings = _.cloneDeep(chartLevelDataLabelSchema).map((setting) => {
            applyTranslations(setting);
            updateValues(chart, chartModel, null, null, null, setting, currentProperties, 'dataLabelProperties');
            updateRunTimeProperties(setting, null, currentProperties, null, chartModel, null, chart, null);
            return applyVisibilityRules({
                setting,
                currentColumn: null,
                currentAxis: null,
                areAdvancedSettingsVisible,
                requiredClientStateProperties: currentProperties,
                chartModel,
                chart,
                setShowAdvancedToggle,
            });
        });
        const filteredSettings = settings.filter(filterVisibleSettings);
        const settingsWithKey = filteredSettings.map((setting, index) => {
            return addRenderKey(setting, null, index);
        });
        finalSettings.push(...settingsWithKey);
        const columnLevelDataLabelSchema = dataLabelSettingSchema.filter((setting) => !setting.isChartProperty);
        getVisualizedColumns(chartModel).forEach((column) => {
            const axis = getAxisForColumn(chartModel, column.id);
            const currentProperties = upgradedClientState.dataLabelProperties[column.id];
            const dataLabelProperties = upgradedClientState.dataLabelProperties[column.id];
            const settings = _.cloneDeep(columnLevelDataLabelSchema).map((setting) => {
                applyTranslations(setting);
                updateValues(chart, chartModel, null, null, column, setting, currentProperties, `dataLabelProperties.${column.id}`);
                updateRunTimeProperties(setting, column, currentProperties, null, chartModel, null, chart, axis);
                return applyVisibilityRules({
                    setting,
                    currentColumn: column,
                    currentAxis: axis,
                    areAdvancedSettingsVisible,
                    requiredClientStateProperties: dataLabelProperties,
                    chartModel,
                    chart,
                    setShowAdvancedToggle,
                    permission,
                });
            });
            const filteredSettings = settings
                .filter(filterVisibleSettings)
                .map((setting, index) => addRenderKey(setting, column, index));
            finalSettings.push(...filteredSettings);
        });
    }
    return finalSettings;
};
export const getTooltipSettingSchema = (chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, setShowAdvancedToggle, permission) => {
    const finalSettings = [];
    const chartType = getChartType(chartModel);
    const isCustomChart = isCustomOrTsHostedChart(chartType);
    const renderPropElements = shouldRenderPropElements(visualPropEditorDefinition);
    const upgradedClientState = upgraderFn(chartModel, customVisualProps, renderPropElements);
    if (isCustomChart) {
        const tooltipSettingSchema = getCustomChartSettingsSchema(visualPropEditorDefinition, SettingsMenuItems.TOOLTIP);
        if (tooltipSettingSchema) {
            const settings = customChartSettingsSchema(tooltipSettingSchema, upgradedClientState.tooltipProperties, chartModel, chart, areAdvancedSettingsVisible, 'tooltipProperties', setShowAdvancedToggle, upgradedClientState);
            finalSettings.push(...settings.filter(filterVisibleSettings));
        }
        return finalSettings;
    }
    const tooltipSettingSchema = getChartSettingsSchema(chartModel, ChartSettingsV2MenuItems.TOOLTIP);
    if (tooltipSettingSchema) {
        const settings = _.cloneDeep(tooltipSettingSchema).map((setting) => {
            applyTranslations(setting);
            updateValues(chart, chartModel, null, null, null, setting, upgradedClientState.tooltipProperties, 'tooltipProperties');
            updateRunTimeProperties(setting, null, upgradedClientState.tooltipProperties, null, chartModel, null, chart, null);
            return applyVisibilityRules({
                setting,
                currentColumn: undefined,
                currentAxis: undefined,
                areAdvancedSettingsVisible,
                requiredClientStateProperties: upgradedClientState.tooltipProperties,
                chartModel,
                chart,
                setShowAdvancedToggle,
                permission,
            });
        });
        finalSettings.push(...settings.filter(filterVisibleSettings));
    }
    return finalSettings;
};
export const getLegendSettingSchema = (chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, setShowAdvancedToggle, permission) => {
    const finalSettings = [];
    const chartType = getChartType(chartModel);
    const isCustomChart = isCustomOrTsHostedChart(chartType);
    const renderPropElements = shouldRenderPropElements(visualPropEditorDefinition);
    const upgradedClientState = upgraderFn(chartModel, customVisualProps, renderPropElements);
    if (isCustomChart) {
        const legendSettingSchema = getCustomChartSettingsSchema(visualPropEditorDefinition, SettingsMenuItems.LEGEND);
        if (legendSettingSchema) {
            const settings = customChartSettingsSchema(legendSettingSchema, upgradedClientState.legendProperties, chartModel, chart, areAdvancedSettingsVisible, 'legendProperties', setShowAdvancedToggle, upgradedClientState);
            finalSettings.push(...settings.filter(filterVisibleSettings));
        }
        return finalSettings;
    }
    const legendSettingSchema = getChartSettingsSchema(chartModel, ChartSettingsV2MenuItems.LEGEND);
    if (legendSettingSchema) {
        const settings = _.cloneDeep(legendSettingSchema).map((setting) => {
            applyTranslations(setting);
            updateValues(chart, chartModel, null, null, null, setting, upgradedClientState.legendProperties, 'legendProperties');
            updateRunTimeProperties(setting, null, upgradedClientState.legendProperties, null, chartModel, null, chart, null);
            return applyVisibilityRules({
                setting,
                currentColumn: undefined,
                currentAxis: undefined,
                areAdvancedSettingsVisible,
                requiredClientStateProperties: upgradedClientState.legendProperties,
                chartModel,
                chart,
                setShowAdvancedToggle,
                permission,
            });
        });
        finalSettings.push(...settings.filter(filterVisibleSettings));
    }
    return finalSettings;
};
export const getDisplaySettingSchema = (chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, setShowAdvancedToggle, permission) => {
    const finalSettings = [];
    const chartType = getChartType(chartModel);
    const isCustomChart = isCustomOrTsHostedChart(chartType);
    const renderPropElements = shouldRenderPropElements(visualPropEditorDefinition);
    const upgradedClientState = upgraderFn(chartModel, customVisualProps, renderPropElements);
    if (isCustomChart) {
        const displaySettingSchema = getCustomChartSettingsSchema(visualPropEditorDefinition, SettingsMenuItems.DISPLAY);
        if (displaySettingSchema) {
            const settings = customChartSettingsSchema(displaySettingSchema, upgradedClientState.displayProperties, chartModel, chart, areAdvancedSettingsVisible, 'displayProperties', setShowAdvancedToggle, upgradedClientState);
            finalSettings.push(...settings.filter(filterVisibleSettings));
        }
        return finalSettings;
    }
    const displaySettingSchema = getChartSettingsSchema(chartModel, ChartSettingsV2MenuItems.DISPLAY);
    const displayProperties = upgradedClientState.displayProperties;
    if (displaySettingSchema) {
        let anyHasAdvancedSettings = false;
        const accumulatingSetShowAdvancedToggle = setShowAdvancedToggle
            ? (has) => {
                anyHasAdvancedSettings = anyHasAdvancedSettings || has;
            }
            : undefined;
        const settings = _.cloneDeep(displaySettingSchema).map((setting) => {
            applyTranslations(setting);
            updateValues(chart, chartModel, null, null, null, setting, displayProperties, 'displayProperties');
            updateRunTimeProperties(setting, null, displayProperties, null, chartModel, null, chart, null);
            return applyVisibilityRules({
                setting,
                currentColumn: undefined,
                currentAxis: undefined,
                areAdvancedSettingsVisible,
                requiredClientStateProperties: displayProperties,
                chartModel,
                chart,
                setShowAdvancedToggle: accumulatingSetShowAdvancedToggle,
                permission,
            });
        });
        setShowAdvancedToggle === null || setShowAdvancedToggle === void 0 ? void 0 : setShowAdvancedToggle(anyHasAdvancedSettings);
        finalSettings.push(...settings.filter(filterVisibleSettings));
    }
    return finalSettings;
};
export const getAxisAlias = (chartModel, axisType) => {
    const chartConfigOptions = configOptions(getChartType(chartModel), chartModel);
    const isXYAxisTransposed = chartConfigOptions.allowedConfigurations.transposeXY;
    const { xAxis, yAxis } = chartConfigOptions.axesAlias;
    switch (axisType) {
        case AxisTypes.X:
            return translate(isXYAxisTransposed ? yAxis : xAxis);
        case AxisTypes.Y:
            return translate(isXYAxisTransposed ? xAxis : yAxis);
        default:
            return '--';
    }
};
export const getAxisSettingsSchema = (chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, selectedTabId, updateSelectedTabId, setShowAdvancedToggle, permission) => {
    const chartType = getChartType(chartModel);
    const isCustomChart = isCustomOrTsHostedChart(chartType);
    const renderPropElements = shouldRenderPropElements(visualPropEditorDefinition);
    const upgradedClientState = upgraderFn(chartModel, customVisualProps, renderPropElements);
    if (isCustomChart) {
        const finalSettings = [];
        const axisSettingSchema = getCustomChartSettingsSchema(visualPropEditorDefinition, SettingsMenuItems.AXIS);
        if (axisSettingSchema) {
            const settings = customChartSettingsSchema(axisSettingSchema, upgradedClientState.axisProperties, chartModel, chart, areAdvancedSettingsVisible, 'axisProperties', setShowAdvancedToggle, upgradedClientState);
            const tabContainer = settings.find(setting => setting.elementType === ChartSettingsElementType.TAB);
            if (selectedTabId && tabContainer) {
                tabContainer.properties.selectedTabId = selectedTabId;
                tabContainer.properties.forceUpdateTabs = true;
                const tabItems = tabContainer.children;
                tabContainer.children = tabItems.map(tabItem => {
                    const newTabItem = structuredClone(tabItem);
                    newTabItem.properties.onTabItemClick = () => {
                        updateSelectedTabId === null || updateSelectedTabId === void 0 ? void 0 : updateSelectedTabId(tabItem.properties.id);
                    };
                    return newTabItem;
                });
            }
            finalSettings.push(...settings.filter(filterVisibleSettings));
        }
        return finalSettings;
    }
    const hydratedAxisSettings = _.cloneDeep(TabSkeleton);
    const axisSettingSchema = getChartSettingsSchema(chartModel, ChartSettingsV2MenuItems.AXIS);
    const axes = getAxes(chartModel);
    const sortedColumnsIds = getSortedColumnsId(chartModel);
    const sortedAxes = [...axes].sort((a, b) => {
        const aIndex = sortedColumnsIds.indexOf(a.properties.linkedColumns[0]);
        const bIndex = sortedColumnsIds.indexOf(b.properties.linkedColumns[0]);
        return aIndex - bIndex;
    });
    const numberOfYAxes = getYAxes(chartModel).length;
    let yAxisIndex = 0;
    sortedAxes.forEach(axis => {
        const tabContent = [];
        const column = getColumn(chartModel, axis.properties.linkedColumns[0]);
        const axisSettings = axisSettingSchema.find((columnSettings) => columnSettings.type === getEffectiveColumnType(column));
        const axisProperty = upgradedClientState.axisProperties[axis.id];
        if (axisSettings === null || axisSettings === void 0 ? void 0 : axisSettings.settings) {
            const settings = _.cloneDeep(axisSettings.settings).map((setting) => {
                applyTranslations(setting);
                updateValues(chart, chartModel, null, null, column, setting, axisProperty, `axisProperties.${axis.id}`, column);
                updateRunTimeProperties(setting, column, axisProperty, upgradedClientState.axisProperties, chartModel, null, chart, axis);
                return applyVisibilityRules({
                    setting,
                    currentColumn: column,
                    currentAxis: axis,
                    areAdvancedSettingsVisible,
                    requiredClientStateProperties: axisProperty,
                    chartModel,
                    chart,
                    setShowAdvancedToggle,
                    permission,
                });
            });
            tabContent.push(...settings.filter(filterVisibleSettings));
        }
        const tabItem = _.cloneDeep(TabItemSkeleton);
        tabItem.properties.id = axis.id;
        tabItem.properties.onTabItemClick = () => {
            updateSelectedTabId === null || updateSelectedTabId === void 0 ? void 0 : updateSelectedTabId(axis.id);
        };
        if (selectedTabId && axis.id === selectedTabId) {
            hydratedAxisSettings[0].properties.selectedTabId = axis.id;
            hydratedAxisSettings[0].properties.forceUpdateTabs = true;
        }
        const suffix = axisProperty.axisType === AxisTypes.Y && numberOfYAxes > 1
            ? `-${++yAxisIndex}`
            : '';
        tabItem.properties.name = `${getAxisAlias(chartModel, axisProperty.axisType)}${suffix}`;
        tabItem.children.push(...tabContent.filter(filterVisibleSettings));
        hydratedAxisSettings[0].children.push(tabItem);
    });
    if (axes.length > 3) {
        hydratedAxisSettings[0].properties.enableOverflowControls = true;
        hydratedAxisSettings[0].properties.className = '';
    }
    return hydratedAxisSettings;
};
export const getSettingsSchema = (chart, chartModel, chartSettingItem, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, allowedCustomConfigurations, selectedTabId, updateSelectedTabId, setShowAdvancedToggle, permission, selectedColumnId) => {
    try {
        setShowAdvancedToggle === null || setShowAdvancedToggle === void 0 ? void 0 : setShowAdvancedToggle(false);
        switch (chartSettingItem) {
            case ChartSettingsV2MenuItems.COLUMN:
                return getColumnSettingSchema(chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, allowedCustomConfigurations, setShowAdvancedToggle, permission, selectedColumnId);
            case ChartSettingsV2MenuItems.DATA_LABEL:
                return getDataLabelSettingSchema(chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, setShowAdvancedToggle, permission);
            case ChartSettingsV2MenuItems.TOOLTIP:
                return getTooltipSettingSchema(chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, setShowAdvancedToggle, permission);
            case ChartSettingsV2MenuItems.LEGEND:
                return getLegendSettingSchema(chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, setShowAdvancedToggle, permission);
            case ChartSettingsV2MenuItems.DISPLAY:
                return getDisplaySettingSchema(chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, setShowAdvancedToggle, permission);
            case ChartSettingsV2MenuItems.AXIS:
                return getAxisSettingsSchema(chartModel, chart, areAdvancedSettingsVisible, visualPropEditorDefinition, customVisualProps, selectedTabId, updateSelectedTabId, setShowAdvancedToggle, permission);
            default:
                logger.warn(`Unsupported chartSettingItem: ${chartSettingItem}`);
                return [];
        }
    }
    catch (error) {
        logger.error('Error in getSettingsSchema', error);
        return [];
    }
};
export const getApplicableSettings = (chartViz, vizType) => {
    const chartType = getChartType(chartViz);
    const alwaysApplicableSettings = [
        ChartSettingsV2MenuItems.CHART_TYPE,
        ChartSettingsV2MenuItems.LAYOUT,
    ];
    const eligibleMenuItemsForSchema = [
        ChartSettingsV2MenuItems.COLUMN,
        ChartSettingsV2MenuItems.AXIS,
        ChartSettingsV2MenuItems.DATA_LABEL,
        ChartSettingsV2MenuItems.TOOLTIP,
        ChartSettingsV2MenuItems.LEGEND,
        ChartSettingsV2MenuItems.DISPLAY,
    ];
    const secondaryActionSettings = [
        ChartSettingsV2MenuItems.QUERY_DETAILS,
        ChartSettingsV2MenuItems.CUSTOM_ACTION,
        ChartSettingsV2MenuItems.R_ANALYSIS,
        ChartSettingsV2MenuItems.MUZE_AI,
    ];
    const filterNonNullValues = (schema) => {
        const nonNullCategories = Object.keys(schema).filter(key => schema[key] !== null);
        return [
            ...alwaysApplicableSettings,
            ...nonNullCategories,
            ...secondaryActionSettings,
        ];
    };
    if (vizType === DisplayMode.ChartMode && chartType) {
        switch (chartType) {
            case ChartType.Bar:
                return filterNonNullValues(barChartSettingsSchema);
            case ChartType.StackedBar:
                return filterNonNullValues(stackedBarSchema);
            case ChartType.LineColumn:
                return filterNonNullValues(lineColumnChartSettingsSchema);
            case ChartType.Column:
                return filterNonNullValues(columnChartSettingsSchema);
            case ChartType.Line:
                return filterNonNullValues(lineChartSettingsSchema);
            case ChartType.StackedColumn:
                return filterNonNullValues(stackedColumnChartSettingsSchema);
            case ChartType.Candlestick:
                return filterNonNullValues(candlestickChartSettingsSchema);
            case ChartType.PivotTable:
                return filterNonNullValues(pivotTableSettingsSchema);
            case ChartType.Area:
                return filterNonNullValues(areaChartSettingsSchema);
            case ChartType.StackedArea:
                return filterNonNullValues(stackedAreaChartSettingsSchema);
            case ChartType.LineStackedColumn:
                return filterNonNullValues(lineStackedColumnChartSettingsSchema);
            case ChartType.Bubble:
                return filterNonNullValues(bubbleChartSettingsSchema);
            case ChartType.Waterfall:
                return filterNonNullValues(waterfallChartSettingsSchema);
            case ChartType.Pareto:
                return filterNonNullValues(paretoChartSettingsSchema);
            case ChartType.GeoHeatmap:
                return filterNonNullValues(geoHeatmapChartSettingsSchema);
            case ChartType.Heatmap:
                return filterNonNullValues(heatmapChartSettingsSchema);
            case ChartType.Sankey:
                return filterNonNullValues(sankeyChartSettingsSchema);
            case ChartType.GeoArea:
                return filterNonNullValues(geoAreaChartSettingsSchema);
            case ChartType.GeoBubble:
                return filterNonNullValues(geoBubbleChartSettingsSchema);
            case ChartType.Pie:
                return filterNonNullValues(pieChartSettingsSchema);
            case ChartType.Funnel:
                return filterNonNullValues(funnelChartSettingsSchema);
            case ChartType.Treemap:
                return filterNonNullValues(treemapChartSettingsSchema);
            case ChartType.SpiderWeb:
                return filterNonNullValues(radarChartSettingsSchema);
            case ChartType.Scatter:
                return filterNonNullValues(scatterChartSettingsSchema);
            case ChartType.Kpi:
                return filterNonNullValues(getKpiChartSettingsSchema(chartViz));
            default:
                logger.warn(`Unsupported chart type: ${chartType}`);
                return [
                    ...alwaysApplicableSettings,
                    ...eligibleMenuItemsForSchema,
                    ...secondaryActionSettings,
                ];
        }
    }
    else if (vizType === DisplayMode.TableMode) {
        return filterNonNullValues(tableSettingsSchema);
    }
    return alwaysApplicableSettings;
};
export const getTableColumnSettings = (tableViz, allSummaryVizes, answerLevelProperty, areAdvancedSettingsVisible, permission, selectedColumnId, setShowAdvancedToggle) => {
    const finalSettings = [];
    const columnSettingSchema = tableSettingsSchema[ChartSettingsV2MenuItems.COLUMN];
    const upgradedClientState = tableUpgraderFn(tableViz);
    const chartLevelColumnSettingsSchema = columnSettingSchema.filter((setting) => !setting.type);
    if (chartLevelColumnSettingsSchema &&
        chartLevelColumnSettingsSchema.length > 0) {
        const currentProperties = upgradedClientState.columnProperties;
        const settings = _.cloneDeep(chartLevelColumnSettingsSchema[0].settings).map((setting) => {
            applyTranslations(setting);
            updateValues(null, null, tableViz, allSummaryVizes, null, setting, currentProperties, 'columnProperties', answerLevelProperty, 'columns');
            updateRunTimeProperties(setting, null, answerLevelProperty, null, null, tableViz, null, null);
            return applyVisibilityRules({
                setting,
                answerLevelProperty,
                currentColumn: null,
                currentAxis: undefined,
                areAdvancedSettingsVisible,
                requiredClientStateProperties: undefined,
                chartModel: tableViz,
                chart: undefined,
                setShowAdvancedToggle,
                permission,
            });
        });
        const settingsWithKey = settings.map((setting, index) => {
            return addRenderKey(setting, null, index);
        });
        finalSettings.push(...settingsWithKey);
    }
    const columnLevelColumnSettingsSchema = columnSettingSchema.filter((setting) => setting.type !== null);
    tableViz === null || tableViz === void 0 ? void 0 : tableViz.columns.forEach((column) => {
        const columnSettings = getColumnSettings(columnLevelColumnSettingsSchema, column.column, false);
        const currentProperties = upgradedClientState.columnProperties[column.id];
        if (columnSettings === null || columnSettings === void 0 ? void 0 : columnSettings.settings) {
            const settings = _.cloneDeep(columnSettings.settings).map((setting) => {
                applyTranslations(setting);
                updateValues(null, null, tableViz, allSummaryVizes, column.column, setting, currentProperties, `columnProperties.${column.id}`, column.column, `columns[0].${column.id}`);
                updateRunTimeProperties(setting, column.column, answerLevelProperty, null, null, tableViz, null, null);
                return applyVisibilityRules({
                    setting,
                    answerLevelProperty,
                    currentColumn: column.column,
                    currentAxis: undefined,
                    areAdvancedSettingsVisible,
                    requiredClientStateProperties: undefined,
                    chartModel: tableViz,
                    chart: undefined,
                    setShowAdvancedToggle,
                    permission,
                });
            });
            const filteredSettings = settings
                .filter(filterVisibleSettings)
                .map((setting, index) => {
                const settingWithKey = addRenderKey(setting, column.column, index);
                addScrollToColumnAttribute(settingWithKey, column.column.id, selectedColumnId);
                return settingWithKey;
            });
            finalSettings.push(...filteredSettings);
        }
    });
    return finalSettings;
};
export const getTableDisplaySettings = (tableViz, allSummaryVizes, areAdvancedSettingsVisible, permission, setShowAdvancedToggle) => {
    const finalSettings = [];
    const displaySettingSchema = tableSettingsSchema[ChartSettingsV2MenuItems.DISPLAY];
    const upgradedClientState = tableUpgraderFn(tableViz);
    const displayProperties = upgradedClientState.displayProperties;
    if (displaySettingSchema) {
        const settings = _.cloneDeep(displaySettingSchema).map((setting) => {
            applyTranslations(setting);
            return updateValues(null, null, tableViz, allSummaryVizes, null, setting, displayProperties, 'displayProperties');
        });
        finalSettings.push(...settings.filter(filterVisibleSettings));
    }
    return finalSettings;
};
export const getTableSettingsSchema = (tableViz, allSummaryVizes, answerLevelProperty, areAdvancedSettingsVisible, settingItem, permission, selectedColumnId, setShowAdvancedToggle) => {
    switch (settingItem) {
        case ChartSettingsV2MenuItems.COLUMN:
            return getTableColumnSettings(tableViz, allSummaryVizes, answerLevelProperty, areAdvancedSettingsVisible, permission, selectedColumnId, setShowAdvancedToggle);
        case ChartSettingsV2MenuItems.DISPLAY:
            return getTableDisplaySettings(tableViz, allSummaryVizes, areAdvancedSettingsVisible, permission, setShowAdvancedToggle);
        case ChartSettingsV2MenuItems.AXIS:
        case ChartSettingsV2MenuItems.DATA_LABEL:
        case ChartSettingsV2MenuItems.LEGEND:
        case ChartSettingsV2MenuItems.TOOLTIP:
            return null;
        default:
            logger.warn(`Unsupported tableSettingItem: ${settingItem}`);
            return [];
    }
};
//# sourceMappingURL=schema-hydrator.js.map